import { cs } from '../../../lib/currency';
import { InvoiceStatement } from './InvoiceStatement';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Receipt, CreditCard, Banknote, Copy, Check, Printer, Ban, Wrench, ShieldCheck, RefreshCw, CheckCircle2 } from 'lucide-react';
import { format, parseISO, isValid } from 'date-fns';
import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { Link } from 'react-router-dom';
import { apiOrigin } from '../../../services/apiOrigin';
import { PlayerLinks } from '../MoneyLinks';
import { getTypeBadge, getStatusBadge } from './utils.jsx';
import { friendlyInvoiceNumber } from '../../../utils/invoiceNumber';
import { useBusinessTimezone } from '../../../utils/businessTime';
import VoidMiDialog from './VoidMiDialog.jsx';
import AdminMiFieldEditDialog from './AdminMiFieldEditDialog.jsx';
import EditInvoiceDialog from './EditInvoiceDialog.jsx';

// iter306p: harden against null/invalid ISO date strings — production
// has a few legacy invoices whose `created_at` / `paid_at` are stored
// as date-only ("2026-06-01") or as a malformed value that crashes
// `parseISO`. This helper returns a safe "—" fallback so the View
// dialog can never blow up the React tree.
//
// iter306gg: now timezone-aware. For the common "MMM d, yyyy h:mm a"
// + "MMM d, yyyy" patterns we route through Intl.DateTimeFormat with
// `timeZone` so an admin in PT sees the same wall-clock time the
// payment was recorded in ET.
const INTL_OPTS_FOR_FMT = {
  'MMM d, yyyy h:mm a': {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  },
  'MMM d, yyyy': { month: 'short', day: 'numeric', year: 'numeric' },
};
function safeDate(value, fmt = 'MMM d, yyyy h:mm a', tz) {
  if (!value) return '—';
  try {
    const d = parseISO(String(value));
    if (!isValid(d)) return String(value);
    const intlOpts = INTL_OPTS_FOR_FMT[fmt];
    if (intlOpts && tz) {
      return new Intl.DateTimeFormat('en-US', { ...intlOpts, timeZone: tz }).format(d);
    }
    return format(d, fmt);
  } catch (_) {
    return String(value);
  }
}

function safeMoney(value) {
  const n = Number(value);
  if (!Number.isFinite(n)) return '0.00';
  return n.toFixed(2);
}

// iter306t: tiny copy-to-clipboard pill used for Invoice ID + Stripe PI
// + Entity ID so admins can paste these into Stripe / a support ticket
// when debugging phantom-paid / mismatched rows like Gaurav's.
function CopyableId({ label, value, testId, href = null }) {
  const [copied, setCopied] = useState(false);
  if (!value) return null;
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(String(value));
      setCopied(true);
      toast.success(`${label} copied`);
      setTimeout(() => setCopied(false), 1500);
    } catch (_) {
      toast.error('Copy failed — select and copy manually');
    }
  };
  return (
    <div>
      <p className="text-sm text-slate-500">
        {label}
        {href && (
          <>
            {' · '}
            <Link
              to={href}
              className="text-emerald-700 hover:underline text-xs"
              data-testid={testId ? `${testId}-open` : undefined}
            >
              open
            </Link>
          </>
        )}
      </p>
      <button
        type="button"
        onClick={onCopy}
        className="mt-0.5 inline-flex items-center gap-1.5 max-w-full font-mono text-xs text-slate-700 bg-slate-100 hover:bg-slate-200 transition-colors rounded px-2 py-1"
        title={`Click to copy ${label}`}
        data-testid={testId}
      >
        <span className="truncate">{String(value)}</span>
        {copied ? (
          <Check className="h-3.5 w-3.5 text-green-600 shrink-0" />
        ) : (
          <Copy className="h-3.5 w-3.5 text-slate-400 shrink-0" />
        )}
      </button>
    </div>
  );
}

/**
 * Read-only invoice details with shortcut actions for unpaid rows.
 */
export default function InvoiceViewDialog({
  open, onOpenChange, invoice, onCharge, onMarkPaid, onMarkSettled,
}) {
  const businessTz = useBusinessTimezone();
  // iter334ag — Nested "Void MI" dialog. Opened from the footer
  // button; validates the linked invoice / Stripe charge is neutralised
  // (void or fully refunded) before allowing the MI void.
  const [voidOpen, setVoidOpen] = useState(false);
  // iter334aj — Admin MI field-edit dialog (PI / cc_surcharge /
  // payment_method / notes) — with PI-uniqueness guard.
  const [fieldEditOpen, setFieldEditOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  // iter334ca — Live Stripe truth for the refund breakdown. When
  // this invoice has a `stripe_payment_intent_id`, we fetch the PI
  // from Stripe on modal open and OVERRIDE the DB-side
  // `amount_charged` / `refunded_amount` values with what Stripe
  // reports. This closes the drift that caused:
  //   • invoice 9d2733e0 to show a $200 refund ghost (Stripe had 0)
  //   • invoice 4d813cb3 ($500+$15 charged, $200 refunded) to render
  //     Net $100 instead of $315 because DB amount_charged was stale
  //   • invoice 17b71a10 (fee reduced $500→$300, refund $215) to
  //     render Charged $300 / Net $85 instead of Charged $515 / Net $300
  // Falls back to DB values if the fetch fails.
  const [breakdown, setBreakdown] = useState(null);
  const [breakdownLoading, setBreakdownLoading] = useState(false);
  const [breakdownError, setBreakdownError] = useState(null);
  const [truthRefreshTick, setTruthRefreshTick] = useState(0);
  const [syncing, setSyncing] = useState(false);
  const piId = invoice?.stripe_payment_intent_id;
  // iter358 — a Stripe-settled invoice can be reconciled even when the PI
  // field is empty (e.g. only the checkout session id was stored, or the
  // PI lives on a linked payment transaction). Show/enable the refresh
  // icon whenever ANY Stripe handle exists so a wrongly-"paid" row can
  // always be re-checked against Stripe.
  const stripeHandle = piId || invoice?.stripe_session_id || invoice?.stripe_txn_id;
  const refundMethod = String(invoice?.last_refund_method || '').toLowerCase();
  // Which row to fetch the base/surcharge breakdown for + sync into.
  const syncEntityType = invoice?.type === 'enrollment'
    ? 'monthly_invoice'
    : invoice?.entity_type_for_sync;
  const syncEntityId = invoice?.type === 'enrollment'
    ? (invoice?.monthly_invoice_id || invoice?.id)
    : invoice?.entity_id;
  const hasRefund = Number(invoice?.refunded_amount || 0) > 0.005;
  const shouldFetchBreakdown =
    open && syncEntityType && syncEntityId
    && ['paid', 'refunded', 'partially_refunded'].includes(invoice?.status);
  useEffect(() => {
    if (!shouldFetchBreakdown) {
      setBreakdown(null);
      setBreakdownError(null);
      return;
    }
    let cancelled = false;
    setBreakdownLoading(true);
    setBreakdownError(null);
    // Use apiOrigin() (same origin the app loaded from) — NOT the
    // build-time REACT_APP_BACKEND_URL, which on the deployed build points
    // at the preview host and made this fetch fail with the production
    // token ("⚠ Showing DB values — Stripe verify failed").
    const backend = apiOrigin();
    const token = localStorage.getItem('token') || localStorage.getItem('access_token');
    fetch(`${backend}/api/admin/billing/refund-breakdown/${syncEntityType}/${syncEntityId}`, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    })
      .then(async (r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((json) => { if (!cancelled) setBreakdown(json); })
      .catch((err) => { if (!cancelled) setBreakdownError(err.message || String(err)); })
      .finally(() => { if (!cancelled) setBreakdownLoading(false); });
    return () => { cancelled = true; };
  }, [shouldFetchBreakdown, syncEntityType, syncEntityId, truthRefreshTick]);

  // An invoice is "paid-like" when Stripe already captured money for it.
  // For those we correct amounts/refunds (and downgrade if the PI later
  // failed) via stripe-truth-sync-one. For unpaid / overdue / pending /
  // processing invoices that carry a PI we instead run reconcile-by-pi,
  // which marks the invoice paid ONLY when the PI actually succeeded and
  // records the payment in the ledger (the proper upgrade path).
  const isPaidLike = ['paid', 'refunded', 'partially_refunded'].includes(invoice?.status);

  const _extractDetail = (body, fallback) => {
    const d = body?.detail;
    if (typeof d === 'string') return d;
    if (d && typeof d === 'object') return d.message || d.detail || fallback;
    return fallback;
  };

  // Sync icon — re-pull Stripe truth for this invoice via the entity-based
  // truth-sync, which now handles EVERY direction from the PaymentIntent:
  //   • succeeded  → mark paid (upgrade an overdue/unpaid invoice)
  //   • processing → ACH in-flight → show PROCESSING (not overdue, not paid)
  //   • canceled / expired / failed → revert a wrongly-"paid" row to unpaid
  //   • refunds    → correct charged / refunded amounts
  const handleSyncFromStripe = async () => {
    if (!syncEntityType || !syncEntityId) {
      toast.error('This invoice type does not support Stripe sync.');
      return;
    }
    setSyncing(true);
    try {
      const backend = apiOrigin();
      const token = localStorage.getItem('token') || localStorage.getItem('access_token');
      const r = await fetch(`${backend}/api/admin/billing/stripe-truth-sync-one`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ entity_type: syncEntityType, entity_id: syncEntityId }),
      });
      const body = await r.json();
      if (!r.ok) {
        toast.error(_extractDetail(body, `Sync failed (HTTP ${r.status})`));
        return;
      }
      const ns = String(body?.new_status || '').toLowerCase();
      const piStatus = String(body?.pi_status || '').toLowerCase();
      if (ns === 'processing' || piStatus === 'processing') {
        toast.success('Synced — bank payment is processing (not yet cleared).');
      } else if (ns === 'paid') {
        toast.success('Synced from Stripe — invoice marked paid.');
      } else if (ns === 'unpaid' || ns === 'pending') {
        toast.success('Synced — Stripe never captured this payment; invoice set back to unpaid.');
      } else if (ns === 'refunded') {
        toast.success('Synced — invoice reflects the Stripe refund.');
      } else {
        toast.success('Synced from Stripe — invoice updated.');
      }
      setTruthRefreshTick((t) => t + 1);
      if (typeof window !== 'undefined') {
        setTimeout(() => window.location.reload(), 800);
      }
    } catch (e) {
      toast.error(String(e?.message || e));
    } finally {
      setSyncing(false);
    }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="invoice-details-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5 text-blue-600" />
            <span>Invoice Details</span>
            {friendlyInvoiceNumber(invoice) && (
              <span
                className="ml-1 font-mono text-xs bg-blue-50 text-blue-700 border border-blue-200 rounded px-2 py-0.5"
                data-testid="invoice-detail-friendly-number"
              >
                {friendlyInvoiceNumber(invoice)}
              </span>
            )}
          </DialogTitle>
          <DialogDescription>
            Full record for this invoice — including parent, players,
            amounts, status, and payment history.
          </DialogDescription>
        </DialogHeader>
        {invoice && (
          <div className="space-y-4">
            {/* iter306jj: surface an ACH-in-flight banner when an
                admin charge has started but Stripe hasn't settled yet.
                Without this, ACH invoices look "Overdue" for the 3-5
                business day clearing window. */}
            {invoice.status === 'processing' && (
              <div
                className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2"
                data-testid="invoice-detail-processing-banner"
              >
                <span className="mt-0.5">⏳</span>
                <div>
                  <p className="font-semibold">Bank transfer in progress</p>
                  <p className="text-xs text-amber-800 mt-0.5">
                    A bank charge for this invoice has been initiated and
                    will settle in 3–5 business days. The invoice will
                    flip to <strong>Paid</strong> automatically once Stripe
                    confirms the transfer.
                  </p>
                </div>
              </div>
            )}
            {invoice.status === 'failed' && (
              <div
                className="rounded-md border border-rose-200 bg-rose-50 px-4 py-3 text-sm text-rose-900 flex items-start gap-2"
                data-testid="invoice-detail-failed-banner"
              >
                <span className="mt-0.5">⚠️</span>
                <div>
                  <p className="font-semibold">Auto-pay couldn&apos;t collect — money still owed</p>
                  <p className="text-xs text-rose-800 mt-0.5">
                    {invoice.last_auto_pay_error
                      ? <>Auto-pay was attempted {invoice.auto_pay_attempts || 'several'} time(s); the last attempt failed with: <strong>{invoice.last_auto_pay_error}</strong>. </>
                      : <>Auto-pay ran {invoice.auto_pay_attempts || 'several'} scheduled attempt(s), but each time there was <strong>no saved card or bank on file to charge</strong> — so nothing ever reached Stripe (that&apos;s why you see attempts but no payment). </>}
                    Add or confirm a payment method, then use <strong>Charge Card</strong> below to collect now, or <strong>Mark Paid</strong> to reconcile manually.
                  </p>
                </div>
              </div>
            )}
            <div className="bg-slate-50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-start">
                <div>
                  <p className="text-sm text-slate-500">Invoice Type</p>
                  <div className="mt-1">{getTypeBadge(invoice.type, invoice.type_label)}</div>
                </div>
                <div className="text-right">
                  <p className="text-sm text-slate-500">Status</p>
                  <div className="mt-1 flex items-center justify-end gap-2">
                    {getStatusBadge(invoice.status)}
                    {stripeHandle && syncEntityType && syncEntityId && (
                      <button
                        type="button"
                        onClick={handleSyncFromStripe}
                        disabled={syncing}
                        title={isPaidLike
                          ? 'Sync this invoice from Stripe (updates charged / refunded amounts, or reverses it if the payment expired)'
                          : 'Reconcile this invoice from Stripe (marks it paid if the payment intent succeeded)'}
                        className="inline-flex items-center justify-center h-6 w-6 rounded-full text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-colors disabled:opacity-50"
                        data-testid="invoice-stripe-sync-button"
                      >
                        <RefreshCw className={`h-3.5 w-3.5 ${syncing ? 'animate-spin' : ''}`} />
                      </button>
                    )}
                  </div>
                  {invoice.status === 'overdue' && invoice.payment_failure_reason && (
                    <p className="text-xs text-red-700 mt-1" data-testid="invoice-view-failure-reason">
                      Payment failed: {invoice.payment_failure_reason}
                    </p>
                  )}
                </div>
              </div>

              <div className="border-t pt-3">
                <p className="text-sm text-slate-500">Description</p>
                <p className="font-medium">{invoice.description}</p>
              </div>

              {/* iter335 — Itemised line items (uniform orders + any
                  invoice carrying a line_items array). Quantity × unit
                  price per row. */}
              {Array.isArray(invoice.line_items) && invoice.line_items.length > 0 && (
                <div className="border-t pt-3" data-testid="invoice-line-items">
                  <p className="text-sm text-slate-500 mb-2">Items</p>
                  <div className="space-y-1">
                    {invoice.line_items.map((li, idx) => (
                      <div
                        key={idx}
                        className="flex justify-between items-center text-sm"
                        data-testid={`invoice-line-item-${idx}`}
                      >
                        <span className="text-slate-700">
                          {li.description}
                          <span className="text-slate-400">
                            {' '}× {li.quantity} @ {cs()}{Number(li.unit_price || 0).toFixed(2)}
                          </span>
                        </span>
                        <span className="font-medium text-slate-800">
                          {cs()}{Number(li.amount || 0).toFixed(2)}
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* iter306t: surface the canonical IDs so admins can
                  reconcile against Stripe / the DB without guessing.
                  Each pill is click-to-copy. */}
              <div className="border-t pt-3 grid grid-cols-1 md:grid-cols-2 gap-3">
                <CopyableId
                  label="Invoice No."
                  value={friendlyInvoiceNumber(invoice) || invoice.invoice_number || invoice.id}
                  testId="invoice-detail-invoice-id"
                />
                {/* Tournament identity: show the Tournament name/id distinct
                    from the Registration ID so the "same id" confusion is
                    resolved (iter360c). */}
                {invoice.type === 'tournament' && (invoice.tournament_name || invoice.tournament_id) && (
                  <CopyableId
                    label="Tournament"
                    value={invoice.tournament_name || invoice.tournament_id}
                    testId="invoice-detail-tournament"
                    href="/admin/tournaments"
                  />
                )}
                {invoice.entity_id && (
                  <CopyableId
                    label={`${invoice.type === 'enrollment' ? 'Enrollment' : invoice.type === 'tournament' ? 'Registration' : invoice.type === 'lane_booking' ? 'Lane Booking' : invoice.type === 'small_group' ? 'Small Group' : invoice.type === 'camp' ? 'Camp Reg' : 'Source'} ID`}
                    value={invoice.entity_id}
                    testId="invoice-detail-entity-id"
                    href={
                      invoice.type === 'enrollment'
                        ? `/admin/enrollments/${invoice.entity_id}`
                        : invoice.type === 'tournament'
                          ? '/admin/tournaments'
                          : invoice.type === 'camp'
                            ? '/admin/camps'
                            : invoice.type === 'lane_booking'
                              ? '/admin/lanes'
                              : null
                    }
                  />
                )}
                {/* iter307df — Surface the underlying monthly_invoice_id
                    so admins have a direct link from the billing-cycle
                    UI to the canonical invoice row (used for Stripe
                    reconciliation + heal/audit endpoints). */}
                {invoice.monthly_invoice_id && invoice.monthly_invoice_id !== invoice.id && (
                  <CopyableId
                    label="Monthly Invoice ID"
                    value={invoice.monthly_invoice_id}
                    testId="invoice-detail-monthly-invoice-id"
                  />
                )}
                {invoice.stripe_payment_intent_id && (
                  <CopyableId
                    label="Stripe Payment Intent"
                    value={invoice.stripe_payment_intent_id}
                    testId="invoice-detail-stripe-pi"
                  />
                )}
                {invoice.stripe_charge_id && (
                  <CopyableId
                    label="Stripe Charge"
                    value={invoice.stripe_charge_id}
                    testId="invoice-detail-stripe-charge"
                  />
                )}
              </div>

              <div className="grid grid-cols-2 gap-4 border-t pt-3">
                <div>
                  <p className="text-sm text-slate-500">Parent</p>
                  {/* iter307di — Hyperlink to the Parent's Billing tab,
                      pre-filtered to this enrollment + MI so admins can
                      jump straight to the billing-cycle row. */}
                  {invoice.parent_id ? (
                    <a
                      href={`/admin/parents/${invoice.parent_id}?tab=billing${invoice.type === 'enrollment' && invoice.entity_id ? `&enrollment=${invoice.entity_id}` : ''}${invoice.monthly_invoice_id ? `&mi=${invoice.monthly_invoice_id}` : ''}`}
                      className="font-medium text-emerald-700 hover:text-emerald-900 hover:underline inline-flex items-center gap-1"
                      data-testid="invoice-detail-parent-link"
                    >
                      {invoice.parent_name}
                    </a>
                  ) : (
                    <p className="font-medium">{invoice.parent_name}</p>
                  )}
                  <p className="text-xs text-slate-500">{invoice.parent_email}</p>
                </div>
                <div>
                  <p className="text-sm text-slate-500">Player(s)</p>
                  <p className="font-medium" data-testid="invoice-detail-players">
                    <PlayerLinks
                      players={(invoice.player_ids || []).map((id, i) => ({
                        id,
                        name: String(invoice.player_names || '').split(',')[i]?.trim(),
                      })).filter((p) => p.name)}
                      names={invoice.player_names}
                    />
                  </p>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4 border-t pt-3">
                <div>
                  <p className="text-sm text-slate-500">Amount</p>
                  <p className="text-xl font-bold text-green-600" data-testid="invoice-detail-amount">
                    {cs()}{safeMoney(breakdown && Number.isFinite(Number(breakdown.current_fee)) ? breakdown.current_fee : invoice.amount)}
                  </p>
                  {invoice.original_amount && invoice.original_amount !== invoice.amount && (
                    <>
                      <p className="text-xs text-slate-400 line-through">Original: {cs()}{safeMoney(invoice.original_amount)}</p>
                      {/* iter322 F-15 — explicit sibling / family discount indicator
                          so parents see WHY the invoice is lower, not just that it is. */}
                      <div
                        className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 px-2 py-0.5 text-[11px] font-medium text-emerald-700"
                        data-testid="invoice-detail-sibling-discount"
                      >
                        <span>Sibling / family discount</span>
                        <span className="text-emerald-600">
                          −{cs()}{safeMoney(Math.max(0, Number(invoice.original_amount || 0) - Number(invoice.amount || 0)))}
                        </span>
                      </div>
                    </>
                  )}
                  {/* iter309d — Per-MI refund tracking lets us display
                      the breakdown on the cycle that was actually
                      refunded (paid → partial refund stays paid;
                      fully refunded flips to status=refunded). */}
                  {/* iter334ca — Refund breakdown driven by Stripe truth
                      (via /admin/billing/stripe-truth-by-pi/{pi_id}).
                      Fallback: DB `refunded_amount` if Stripe not
                      queried yet or the fetch failed. The block
                      renders when EITHER source reports refunds. */}
                  {/* iter335 — Base / surcharge breakdown from
                      /admin/billing/refund-breakdown/{type}/{id}. Anchored
                      to Stripe truth (captured) + the refunds ledger, so it
                      never reads the drift-prone payment_amount field. Shows
                      Base fee / Surcharge charged / Refunded / Refunded
                      surcharge. Surcharge lines are hidden for non-card. */}
                  {(() => {
                    if (!['paid', 'refunded', 'partially_refunded'].includes(invoice.status)) return null;
                    const b = breakdown;
                    const dbRefunded = Number(invoice.refunded_amount || 0);
                    // Nothing refunded from either source → no block.
                    const anyRefund = (b && Number(b.total_refunded) > 0.005) || dbRefunded > 0.005;
                    if (!anyRefund) return null;

                    const methodLabels = {
                      stripe: 'to card', card: 'to card', link: 'to card',
                      credit: 'as account credit', account_credit: 'as account credit',
                      cash: 'as cash', check: 'by check',
                      zelle: 'via Zelle', venmo: 'via Venmo',
                      manual: 'manually', waived: 'waived',
                    };
                    const methodLabel = methodLabels[refundMethod]
                      || methodLabels[String(b?.refund_method || '').toLowerCase()]
                      || (b?.is_card ? 'to card' : '');

                    // Loading / error states before the breakdown arrives.
                    if (!b) {
                      return (
                        <div className="mt-2 text-xs rounded border border-purple-200 bg-purple-50 px-2 py-1.5 text-purple-500" data-testid="invoice-detail-refund-breakdown">
                          {breakdownLoading ? 'Loading refund details…' : (
                            <span className="text-amber-700">
                              ⚠ Showing basic values — tap the sync icon to refresh from Stripe
                            </span>
                          )}
                          <div className="flex justify-between gap-3 text-purple-900 mt-1">
                            <span>Refunded{methodLabel ? ` · ${methodLabel}` : ''}</span>
                            <span className="tabular-nums">−{cs()}{safeMoney(dbRefunded)}</span>
                          </div>
                        </div>
                      );
                    }

                    const baseFee = Number(b.base_fee_charged || 0);
                    const surchargeCharged = Number(b.surcharge_charged || 0);
                    const baseRefunded = Number(b.base_refunded || 0);
                    const surchargeRefunded = Number(b.surcharge_refunded || 0);
                    const currentFee = Number(b.current_fee || 0);
                    const showSurcharge = surchargeCharged > 0.005 || surchargeRefunded > 0.005;

                    return (
                      <div
                        className="mt-2 text-xs space-y-0.5 rounded border border-purple-200 bg-purple-50 px-2 py-1.5"
                        data-testid="invoice-detail-refund-breakdown"
                      >
                        <div className="flex justify-between gap-3 text-purple-900">
                          <span>Base fee</span>
                          <span className="tabular-nums" data-testid="refund-base-fee">{cs()}{safeMoney(baseFee)}</span>
                        </div>
                        {showSurcharge && (
                          <div className="flex justify-between gap-3 text-purple-900">
                            <span>Surcharge charged</span>
                            <span className="tabular-nums" data-testid="refund-surcharge-charged">{cs()}{safeMoney(surchargeCharged)}</span>
                          </div>
                        )}
                        <div className="flex justify-between gap-3 text-purple-900 border-t border-purple-100 pt-0.5">
                          <span>
                            Refunded
                            {methodLabel && <span className="text-purple-500"> · {methodLabel}</span>}
                          </span>
                          <span className="tabular-nums" data-testid="refund-amount-refunded">−{cs()}{safeMoney(baseRefunded)}</span>
                        </div>
                        {showSurcharge && (
                          <div className="flex justify-between gap-3 text-purple-900">
                            <span>Refunded surcharge</span>
                            <span className="tabular-nums" data-testid="refund-surcharge-refunded">−{cs()}{safeMoney(surchargeRefunded)}</span>
                          </div>
                        )}
                        <div className="flex justify-between gap-3 font-semibold text-purple-900 border-t border-purple-200 pt-0.5">
                          <span>Current fee</span>
                          <span className="tabular-nums" data-testid="refund-current-fee">{cs()}{safeMoney(currentFee)}</span>
                        </div>
                        {b.stripe_verified ? (
                          <div
                            className="flex items-center gap-1 pt-1 text-[10px] text-purple-600"
                            data-testid="invoice-detail-refund-stripe-verified"
                          >
                            <ShieldCheck className="h-3 w-3" />
                            <span>Verified from Stripe</span>
                          </div>
                        ) : b.is_card ? (
                          <div
                            className="pt-1 text-[10px] text-amber-700"
                            data-testid="invoice-detail-refund-stripe-error"
                          >
                            ⚠ Stripe not reachable — tap the sync icon to refresh
                          </div>
                        ) : null}
                      </div>
                    );
                  })()}
                  {/* iter306z/dd: split base + processor surcharge when the
                      paid PT row had a surcharge_amount > 0. Derive base
                      from amount − surcharge when backend hasn't denormed it.
                      iter318 wave 24t — Removed the wave 22r display
                      guard (`surcharge <= 0.5 × amount`) that was hiding
                      bogus values. Reasons:
                        1. Print PDF and parent portal never had the guard
                           → parents saw the bogus $198 even when admin
                           modal hid it. Inconsistent behaviour.
                        2. Wave 24s surcharge-patch endpoint now exists,
                           so admins can correct bogus rows directly.
                           Hiding them just delays discovery.
                        3. A visible-but-wrong number in the admin view
                           is a call-to-action; a hidden number is a
                           silent bug. */}
                  {/* iter334ap — Surcharge is a payment-time concept
                      (Pay Now modal only when card is selected). It
                      is NOT an invoice line item. Removed the
                      universal Amount surcharge-split block that
                      showed "<Entity> fee + Card processing fee"
                      for every entity type. Applies uniformly to
                      enrollment / tournament / camp / small_group
                      / lane_booking / manual invoices. */}
                </div>
                <div>
                  <p className="text-sm text-slate-500">Date Created</p>
                  <p className="font-medium">
                    {safeDate(invoice.created_at, 'MMM d, yyyy h:mm a', businessTz)}
                  </p>
                </div>
              </div>

              {/* iter334ad — B3 Invoice Breakdown. Renders an itemised
                  statement (Monthly Fee → Discount → CC Surcharge →
                  Credit Applied → Refund → Balance) from the backend
                  `breakdown` dict. Each line only renders if its value
                  is non-zero, so pending/uncharged rows stay clean.
                  The Balance line reflects the ACTUAL cash retained
                  after credits + refunds — critical for parents who
                  paid partly via account credit (see Parent 0da355c6). */}
              {invoice.accounting && (
                <div className="border-t pt-3">
                  <InvoiceStatement accounting={invoice.accounting} />
                </div>
              )}

              {invoice.admin_notes && (
                <div className="border-t pt-3">
                  <p className="text-sm text-slate-500">Admin Notes</p>
                  <p className="text-sm bg-blue-50 p-2 rounded mt-1">{invoice.admin_notes}</p>
                </div>
              )}

              {invoice.status === 'void' && (
                <div className="border-t pt-3 space-y-2">
                  <p className="text-sm font-medium text-red-600">Invoice Voided</p>
                  {invoice.void_reason && (
                    <p className="text-sm bg-red-50 p-2 rounded">{invoice.void_reason}</p>
                  )}
                  {invoice.voided_at && (
                    <p className="text-xs text-slate-500">
                      Voided on: {safeDate(invoice.voided_at, 'MMM d, yyyy h:mm a', businessTz)}
                    </p>
                  )}
                </div>
              )}

              {invoice.status === 'paid' && (
                <div className="border-t pt-3 space-y-2">
                  <p className="text-sm font-medium text-green-600">Payment Information</p>
                  <div className="grid grid-cols-2 gap-4">
                    {(() => {
                      // iter334ar — Fall back to a card/stripe label
                      // when the invoice's `payment_method` field is
                      // empty but a Stripe PI is stamped. Tournament
                      // (and other Stripe-checkout entities) often
                      // leave payment_method null when the charge came
                      // via Stripe Checkout — the PI presence is the
                      // signal.
                      const pmLabel = invoice.payment_method
                        || (invoice.stripe_payment_intent_id ? 'stripe' : '');
                      const shown = invoice.accounting?.payment_method || pmLabel;
                      if (!shown) return null;
                      return (
                        <div>
                          <p className="text-sm text-slate-500">Payment Method</p>
                          <p className="font-medium capitalize" data-testid="invoice-view-payment-method">{shown.replace(/_/g, ' ')}</p>
                        </div>
                      );
                    })()}
                    {invoice.paid_at && (
                      <div>
                        <p className="text-sm text-slate-500">Paid On</p>
                        <p className="font-medium">
                          {safeDate(invoice.paid_at, 'MMM d, yyyy h:mm a', businessTz)}
                        </p>
                      </div>
                    )}
                    {/* iter334aq — Amount Paid shows the cash actually
                        captured (base + card surcharge) when the invoice
                        was paid via card. Renders only when the paid
                        amount differs from the base amount by >$0.01
                        (i.e. surcharge was captured). Applies uniformly
                        across every entity type. */}
                    {(() => {
                      const paid = Number(invoice.amount_paid || 0);
                      const base = Number(invoice.amount || 0);
                      const surchargeIncluded = paid - base;
                      if (paid <= 0 || Math.abs(surchargeIncluded) < 0.01) return null;
                      return (
                        <div data-testid="invoice-detail-amount-paid">
                          <p className="text-sm text-slate-500">Amount Paid</p>
                          <p className="font-medium tabular-nums">
                            {cs()}{safeMoney(paid)}
                            {surchargeIncluded > 0 && (
                              <span className="text-xs text-slate-500 ml-1">
                                (incl. {cs()}{safeMoney(surchargeIncluded)} card fee)
                              </span>
                            )}
                          </p>
                        </div>
                      );
                    })()}
                    {invoice.reference_number && (
                      <div>
                        <p className="text-sm text-slate-500">Reference #</p>
                        <p className="font-medium">{invoice.reference_number}</p>
                      </div>
                    )}
                  </div>
                  {invoice.notes && (
                    <div>
                      <p className="text-sm text-slate-500">Notes</p>
                      <p className="text-sm">{invoice.notes}</p>
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* iter334as — Footer wraps to multiple rows on narrow
                widths so 6+ action buttons never push the modal off
                screen. Button sequence is preserved across ALL
                invoice types: Close · Print/PDF · Edit MI Fields ·
                Void MI · Charge Card · Mark Paid · Issue Invoice.
                (Type-specific buttons remain hidden when not
                applicable — the visible subset always renders in the
                declared order.) */}
            <DialogFooter className="flex-wrap gap-2 sm:justify-end">
              <Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button>
              <Button
                variant="outline"
                onClick={() => {
                  // iter306x: open the printable invoice in a new tab.
                  // The print view reads `?type=&id=` and pulls full
                  // detail from /admin/invoices/all so we don't need to
                  // serialise the row through the URL.
                  const url = `/admin/invoices/${invoice.id}/print?type=${encodeURIComponent(invoice.type || '')}`;
                  window.open(url, '_blank', 'noopener');
                }}
                data-testid="invoice-print-button"
              >
                <Printer className="h-4 w-4 mr-1" />
                Print / PDF
              </Button>
              {/* iter318 wave 24 — "Issue Invoice" action for paid
                  prorated first-cycle enrollments that never got a
                  canonical monthly_invoices row. Only shows when the
                  invoice `id` equals `entity_id` (i.e. the response
                  fell back to enrollment_id because no MI existed)
                  AND the row is a paid enrollment. Mints the MI on
                  the backend, then downstream reads get a proper
                  invoice with its own id. */}
              {invoice.type === 'enrollment'
                && invoice.status === 'paid'
                && invoice.entity_id
                && invoice.id === invoice.entity_id
                && !invoice.monthly_invoice_id && (
                <Button
                  variant="outline"
                  onClick={async () => {
                    if (!window.confirm(
                      "Mint a canonical monthly invoice for this paid " +
                      "prorated enrollment? The enrollment's payment "+
                      "details (Stripe PI, amount, refund status) will "+
                      "be copied onto the new invoice, and downstream "+
                      "views will use the invoice's own id from now on."
                    )) return;
                    try {
                      const r = await fetch(
                        `/api/admin/enrollments/${invoice.entity_id}/issue-first-cycle-invoice`,
                        { method: 'POST', credentials: 'include' },
                      );
                      const body = await r.json();
                      if (!r.ok) {
                        toast.error(body?.detail || 'Failed to issue invoice');
                        return;
                      }
                      if (body.created) {
                        toast.success(`Invoice ${body.invoice_id.slice(0, 8)}… minted`);
                      } else {
                        toast.info('An invoice already exists for this cycle.');
                      }
                      onOpenChange(false);
                      if (typeof window !== 'undefined') {
                        // Give the toast a moment then reload the parent
                        // view so the new invoice id appears.
                        setTimeout(() => window.location.reload(), 900);
                      }
                    } catch (e) {
                      toast.error(String(e?.message || e));
                    }
                  }}
                  data-testid="invoice-issue-first-cycle-button"
                  className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
                >
                  <Receipt className="h-4 w-4 mr-1" />
                  Issue Invoice
                </Button>
              )}
              {/* iter334aj — Admin MI Field Edit. Only shown for
                  enrollment-type MIs (the underlying doc lives in
                  `monthly_invoices`). Non-blocking regardless of the
                  MI status — some drift repairs need to happen on
                  paid MIs too. */}
              {invoice.type === 'enrollment'
                && (invoice.monthly_invoice_id || invoice.id) && (
                <Button
                  variant="outline"
                  onClick={() => setFieldEditOpen(true)}
                  data-testid="invoice-admin-mi-edit-button"
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                >
                  <Wrench className="h-4 w-4 mr-1" />
                  Edit MI Fields
                </Button>
              )}
              {/* iter334ag — Void MI button. Only shown for
                  enrollment-type invoices (MIs live under
                  monthly_invoices). Not shown for already-void rows.
                  The dialog will preflight against the linked
                  invoice/PI so accidental voids of live charges are
                  blocked. */}
              {invoice.type === 'enrollment'
                && invoice.status !== 'void'
                && (invoice.monthly_invoice_id || invoice.id) && (
                <Button
                  variant="outline"
                  onClick={() => setVoidOpen(true)}
                  data-testid="invoice-void-mi-button"
                  className="border-rose-300 text-rose-700 hover:bg-rose-50"
                >
                  <Ban className="h-4 w-4 mr-1" />
                  Void MI
                </Button>
              )}
              {/* 2026-06 — Edit affordance for NON-enrollment invoice
                  types (tournament, small_group, lane_booking, manual).
                  Enrollment has its own "Edit MI Fields" (above). Lets
                  admins edit amount/notes AND link a Stripe PI to older
                  invoices recorded without one. */}
              {['tournament', 'tournament_registration', 'small_group', 'lane_booking', 'manual'].includes(invoice.type) && (
                <Button
                  variant="outline"
                  onClick={() => setEditOpen(true)}
                  data-testid="invoice-edit-fields-button"
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                >
                  <Wrench className="h-4 w-4 mr-1" />
                  Edit MI Fields
                </Button>
              )}
              {(invoice.status === 'pending' || invoice.status === 'overdue' || invoice.status === 'partial' || invoice.status === 'failed') && (
                <>
                  <Button
                    onClick={() => { onOpenChange(false); onCharge && onCharge(invoice); }}
                    className="bg-blue-600 hover:bg-blue-700"
                    data-testid="invoice-charge-card-btn"
                  >
                    <CreditCard className="h-4 w-4 mr-1" />
                    {invoice.status === 'partial' ? 'Charge Balance' : 'Charge Card'}
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => { onOpenChange(false); onMarkPaid && onMarkPaid(invoice); }}
                    data-testid="invoice-mark-paid-btn"
                  >
                    <Banknote className="h-4 w-4 mr-1" />
                    Mark Paid
                  </Button>
                </>
              )}
              {/* iter369 — Force-settle an invoice that already shows as
                  paid/refunded but is stuck with a stale balance (e.g. the
                  cash/credit payment wasn't itemised). Stops it reappearing
                  in the underpaid scan and suppresses further reminders. */}
              {['paid', 'partially_refunded', 'refunded'].includes(invoice.status) && !invoice.admin_settled && onMarkSettled && (
                <Button
                  variant="outline"
                  onClick={() => { onOpenChange(false); onMarkSettled(invoice); }}
                  data-testid="invoice-mark-settled-btn"
                  className="border-emerald-300 text-emerald-700 hover:bg-emerald-50"
                >
                  <CheckCircle2 className="h-4 w-4 mr-1" />
                  Mark Settled
                </Button>
              )}
              {invoice.admin_settled && (
                <span className="inline-flex items-center text-xs text-emerald-600 px-2" data-testid="invoice-settled-badge">
                  <CheckCircle2 className="h-3.5 w-3.5 mr-1" /> Settled
                </span>
              )}
            </DialogFooter>
          </div>
        )}
      </DialogContent>
      {/* iter334ag — Nested void confirm dialog. */}
      <VoidMiDialog
        open={voidOpen}
        onOpenChange={setVoidOpen}
        invoice={invoice}
        onVoided={() => {
          // Close the details modal + trigger a reload so the parent
          // list reflects the new void status.
          onOpenChange(false);
          if (typeof window !== 'undefined') {
            setTimeout(() => window.location.reload(), 700);
          }
        }}
      />
      {/* iter334aj — Nested MI field edit dialog. */}
      <AdminMiFieldEditDialog
        open={fieldEditOpen}
        onOpenChange={setFieldEditOpen}
        invoice={invoice}
        onEdited={() => {
          onOpenChange(false);
          if (typeof window !== 'undefined') {
            setTimeout(() => window.location.reload(), 700);
          }
        }}
      />
      {/* 2026-06 — Edit dialog for non-enrollment invoice types. */}
      <EditInvoiceDialog
        open={editOpen}
        onOpenChange={setEditOpen}
        invoice={invoice}
        onEdited={() => {
          onOpenChange(false);
          if (typeof window !== 'undefined') {
            setTimeout(() => window.location.reload(), 700);
          }
        }}
      />
    </Dialog>
  );
}
